// ============================================
// MEDFLOW - Agents Page — Chat Simulator
// ============================================

import { useState, useRef, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Bot,
  Send,
  RotateCcw,
  Wifi,
  WifiOff,
  MessageSquare,
  ChevronRight,
  Zap,
  Building2,
  Mic,
  MicOff,
  Volume2,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { professionalsApi, chatApi } from '@/lib/api';
import { cn, getInitials } from '@/lib/utils';
import { useVoiceChat } from '@/hooks/useVoiceChat';

interface Professional {
  id: string;
  color: string;
  specialty: string;
  user: { name: string };
}

interface AgentConfig {
  id: string;
  professionalId: string;
  name: string;
  channel: string;
  isActive: boolean;
  n8nWebhookUrl?: string;
  lineIdentifier?: string;
}

interface ChatMessage {
  role: 'user' | 'agent';
  content: string;
  time: string;
  isLoading?: boolean;
}

const OFFICIAL_AGENT_ID = 'official';

const QUICK_MESSAGES = [
  'Hola, quiero una hora',
  '¿Qué servicios tienen?',
  '¿Cuándo tienen disponibilidad?',
  '¿Cuánto cuesta la consulta?',
];

export function AgentsPage() {
  const [selectedProfId, setSelectedProfId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sessionSuffix, setSessionSuffix] = useState(() => Math.random().toString(36).slice(2, 10));
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const voice = useVoiceChat();
  const voiceModeRef = useRef(voice.isVoiceMode);
  voiceModeRef.current = voice.isVoiceMode;

  const { data: professionals = [] } = useQuery({
    queryKey: ['professionals'],
    queryFn: async () => {
      const res = await professionalsApi.list(true);
      return (res.data as Professional[]) || [];
    },
  });

  const { data: agents = [] } = useQuery({
    queryKey: ['professional-agents'],
    queryFn: async () => {
      const res = await professionalsApi.listAgents();
      return (res.data as AgentConfig[]) || [];
    },
  });

  const agentMap = new Map(agents.map((a) => [a.professionalId, a]));
  const isOfficialSelected = selectedProfId === OFFICIAL_AGENT_ID;
  const selectedAgent = isOfficialSelected ? undefined : (selectedProfId ? agentMap.get(selectedProfId) : undefined);
  const selectedProf = isOfficialSelected ? undefined : professionals.find((p) => p.id === selectedProfId);

  const sendMutation = useMutation({
    mutationFn: async (message: string) => {
      const profId = selectedProfId!;
      const res = await chatApi.test(profId, message, sessionSuffix);
      return res.data;
    },
    onMutate: (message) => {
      const now = new Date().toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: message, time: now },
        { role: 'agent', content: '', time: '', isLoading: true },
      ]);
      setInput('');
    },
    onSuccess: (data) => {
      const now = new Date().toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
      const reply = data?.reply ?? '(Sin respuesta del agente)';
      setMessages((prev) => [
        ...prev.filter((m) => !m.isLoading),
        { role: 'agent', content: reply, time: now },
      ]);
      // Auto-speak agent reply when voice mode is active
      if (voiceModeRef.current && reply && reply !== '(Sin respuesta del agente)') {
        voice.speakText(reply);
      }
    },
    onError: (error: Error) => {
      const now = new Date().toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
      setMessages((prev) => [
        ...prev.filter((m) => !m.isLoading),
        { role: 'agent', content: `Error: ${error.message}`, time: now },
      ]);
    },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-send transcript when speech recognition finishes
  const prevListeningRef = useRef(false);
  useEffect(() => {
    const wasListening = prevListeningRef.current;
    prevListeningRef.current = voice.isListening;

    if (wasListening && !voice.isListening && voice.interimTranscript.trim() && !sendMutation.isPending) {
      sendMutation.mutate(voice.interimTranscript.trim());
    }
  }, [voice.isListening, voice.interimTranscript, sendMutation]);

  const handleSend = () => {
    if (!input.trim() || !selectedProfId || sendMutation.isPending) return;
    sendMutation.mutate(input.trim());
  };

  const handleNewConversation = () => {
    setMessages([]);
    setSessionSuffix(Math.random().toString(36).slice(2, 10));
    inputRef.current?.focus();
  };

  const handleSelectProfessional = (profId: string) => {
    if (profId === selectedProfId) return;
    setSelectedProfId(profId);
    setMessages([]);
    setSessionSuffix(Math.random().toString(36).slice(2, 10));
  };

  const canChat = isOfficialSelected || !!selectedAgent?.n8nWebhookUrl;

  return (
    <div className="flex h-[calc(100vh-8rem)] gap-0 rounded-xl border overflow-hidden bg-white shadow-sm">
      {/* ── Left panel: professional list ── */}
      <div className="w-72 shrink-0 border-r flex flex-col">
        <div className="px-4 py-4 border-b bg-gray-50">
          <h1 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <Bot className="w-4 h-4 text-primary" />
            Agentes IA
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            {agents.length} configurado{agents.length !== 1 ? 's' : ''} ·{' '}
            {agents.filter((a) => a.n8nWebhookUrl).length} con webhook
          </p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Official clinic agent */}
          <button
            onClick={() => handleSelectProfessional(OFFICIAL_AGENT_ID)}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors border-b',
              isOfficialSelected
                ? 'bg-primary/5 border-r-2 border-primary'
                : 'hover:bg-gray-50 border-r-2 border-transparent'
            )}
          >
            <div className="w-9 h-9 rounded-full flex items-center justify-center bg-gradient-to-br from-primary to-primary/70 text-white shrink-0">
              <Building2 className="w-4.5 h-4.5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-sm text-gray-900 truncate">Recepcionista Oficial</p>
              <div className="flex items-center gap-1 mt-0.5">
                <Wifi className="w-3 h-3 text-green-500 shrink-0" />
                <span className="text-xs text-gray-500 truncate">Agente de la clínica</span>
              </div>
            </div>
            {isOfficialSelected && <ChevronRight className="w-4 h-4 text-primary shrink-0" />}
          </button>

          {/* Per-professional agents */}
          <div className="divide-y">
            {professionals.map((prof) => {
              const agent = agentMap.get(prof.id);
              const isSelected = selectedProfId === prof.id;
              const hasWebhook = !!agent?.n8nWebhookUrl;

              return (
                <button
                  key={prof.id}
                  onClick={() => handleSelectProfessional(prof.id)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
                    isSelected
                      ? 'bg-primary/5 border-r-2 border-primary'
                      : 'hover:bg-gray-50 border-r-2 border-transparent'
                  )}
                >
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-xs shrink-0"
                    style={{ backgroundColor: prof.color }}
                  >
                    {getInitials(prof.user.name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm text-gray-900 truncate">{prof.user.name}</p>
                    {agent ? (
                      <div className="flex items-center gap-1 mt-0.5">
                        {hasWebhook ? (
                          <Wifi className="w-3 h-3 text-green-500 shrink-0" />
                        ) : (
                          <WifiOff className="w-3 h-3 text-orange-400 shrink-0" />
                        )}
                        <span className="text-xs text-gray-500 truncate">{agent.name}</span>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 mt-0.5 italic">Sin agente</p>
                    )}
                  </div>
                  {isSelected && <ChevronRight className="w-4 h-4 text-primary shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Right panel: chat ── */}
      {!selectedProfId ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-400 bg-gray-50">
          <MessageSquare className="w-12 h-12 opacity-20" />
          <p className="text-sm">Selecciona un profesional para probar su agente</p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0">
          {/* Chat header */}
          <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50 shrink-0">
            <div className="flex items-center gap-3">
              {isOfficialSelected ? (
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-br from-primary to-primary/70 text-white shrink-0">
                  <Building2 className="w-4 h-4" />
                </div>
              ) : (
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-xs shrink-0"
                  style={{ backgroundColor: selectedProf?.color }}
                >
                  {getInitials(selectedProf?.user.name ?? '')}
                </div>
              )}
              <div>
                <p className="font-semibold text-sm text-gray-900">
                  {isOfficialSelected ? 'Recepcionista Oficial' : (selectedAgent?.name ?? 'Sin agente configurado')}
                </p>
                <p className="text-xs text-gray-500">
                  {isOfficialSelected
                    ? 'Agente oficial de la clínica · whatsapp'
                    : `${selectedProf?.user.name}${selectedAgent ? ` · ${selectedAgent.channel}` : ''}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Voice mode toggle */}
              {voice.isSTTSupported && canChat && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={voice.toggleVoiceMode}
                  className={cn(
                    'h-8 w-8 p-0 rounded-lg transition-colors',
                    voice.isVoiceMode
                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      : 'text-gray-400 hover:text-gray-600'
                  )}
                  title={voice.isVoiceMode ? 'Desactivar modo voz' : 'Activar modo voz'}
                >
                  {voice.isVoiceMode ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
                </Button>
              )}
              {isOfficialSelected ? (
                <Badge className="bg-green-100 text-green-700 text-xs font-medium">
                  <Wifi className="w-3 h-3 mr-1" /> Conectado
                </Badge>
              ) : selectedAgent?.n8nWebhookUrl ? (
                <Badge className="bg-green-100 text-green-700 text-xs font-medium">
                  <Wifi className="w-3 h-3 mr-1" /> Conectado
                </Badge>
              ) : selectedAgent ? (
                <Badge className="bg-orange-100 text-orange-700 text-xs font-medium">
                  <WifiOff className="w-3 h-3 mr-1" /> Sin webhook
                </Badge>
              ) : null}
              {messages.length > 0 && (
                <Button variant="ghost" size="sm" onClick={handleNewConversation} className="text-xs h-7">
                  <RotateCcw className="w-3 h-3 mr-1" /> Nueva conv.
                </Button>
              )}
            </div>
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
            {!isOfficialSelected && !selectedAgent ? (
              <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
                <Bot className="w-12 h-12 text-gray-200" />
                <p className="text-sm text-gray-500 font-medium">Sin agente configurado</p>
                <p className="text-xs text-gray-400">
                  Ve a Profesionales → botón "Crear agente"
                </p>
              </div>
            ) : !canChat ? (
              <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
                <WifiOff className="w-12 h-12 text-orange-200" />
                <p className="text-sm text-gray-600 font-medium">Webhook n8n no configurado</p>
                <p className="text-xs text-gray-400 max-w-xs">
                  El agente existe pero no tiene workflow en n8n. Ve a Profesionales → Configurar agente → Sincronizar n8n.
                </p>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <Zap className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Simulador activo</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Los mensajes se procesan en tiempo real por el agente n8n
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 justify-center max-w-sm">
                  {QUICK_MESSAGES.map((q) => (
                    <button
                      key={q}
                      onClick={() => {
                        setInput(q);
                        inputRef.current?.focus();
                      }}
                      className="text-xs border rounded-full px-3 py-1.5 text-gray-600 hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg, i) => (
                <div key={i} className={cn('flex items-end gap-2', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                  {msg.role === 'agent' && (
                    <div className={cn(
                      'w-6 h-6 rounded-full flex items-center justify-center shrink-0',
                      isOfficialSelected ? 'bg-gradient-to-br from-primary to-primary/70' : 'bg-primary/10'
                    )}>
                      {isOfficialSelected
                        ? <Building2 className="w-3.5 h-3.5 text-white" />
                        : <Bot className="w-3.5 h-3.5 text-primary" />}
                    </div>
                  )}
                  <div
                    className={cn(
                      'max-w-[72%] rounded-2xl px-4 py-2.5 text-sm',
                      msg.role === 'user'
                        ? 'bg-primary text-white rounded-br-sm'
                        : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                    )}
                  >
                    {msg.isLoading ? (
                      <span className="flex gap-1 items-center py-0.5">
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:0ms]" />
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:150ms]" />
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:300ms]" />
                      </span>
                    ) : (
                      <>
                        <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                        <div className={cn('flex items-center gap-2 mt-1.5', msg.role === 'user' ? 'justify-end' : 'justify-between')}>
                          {msg.role === 'agent' && (
                            <button
                              onClick={() => voice.speakText(msg.content)}
                              className={cn(
                                'flex items-center gap-1 text-xs transition-colors',
                                voice.isSpeaking ? 'text-blue-500' : 'text-gray-400 hover:text-gray-600'
                              )}
                              title="Reproducir en voz alta"
                            >
                              <Volume2 className="w-3 h-3" />
                            </button>
                          )}
                          {msg.time && (
                            <p className={cn('text-xs', msg.role === 'user' ? 'text-white/60' : 'text-gray-400')}>
                              {msg.time}
                            </p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-4 py-3 border-t bg-white shrink-0">
            {voice.isVoiceMode && canChat ? (
              /* Voice input mode */
              <div className="flex flex-col items-center gap-2 py-2">
                <button
                  onClick={() => {
                    if (voice.isListening) {
                      voice.stopListening();
                    } else {
                      voice.stopSpeaking();
                      voice.startListening();
                    }
                  }}
                  disabled={sendMutation.isPending}
                  className={cn(
                    'w-14 h-14 rounded-full flex items-center justify-center transition-all duration-200',
                    voice.isListening
                      ? 'bg-red-100 text-red-600 animate-pulse shadow-lg shadow-red-100'
                      : sendMutation.isPending
                        ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                        : 'bg-gray-100 text-gray-500 hover:bg-blue-50 hover:text-blue-600 hover:shadow-md'
                  )}
                >
                  <Mic className="w-6 h-6" />
                </button>
                <p className="text-xs text-gray-400">
                  {sendMutation.isPending
                    ? 'Procesando...'
                    : voice.isListening
                      ? 'Escuchando...'
                      : 'Presiona para hablar'}
                </p>
                {voice.interimTranscript && (
                  <p className="text-sm text-gray-500 italic text-center max-w-xs">
                    {voice.interimTranscript}
                  </p>
                )}
              </div>
            ) : (
              /* Text input mode */
              <div className="flex gap-2 items-end">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder={
                    canChat ? 'Escribe un mensaje... (Enter para enviar)' : 'Sincroniza el webhook primero'
                  }
                  disabled={!canChat || sendMutation.isPending}
                  rows={1}
                  className="flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:bg-gray-50"
                />
                <Button
                  onClick={handleSend}
                  disabled={!input.trim() || !canChat || sendMutation.isPending}
                  className="h-10 w-10 p-0 rounded-xl shrink-0"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
